import { useEffect, useRef, useState, useCallback } from 'react';

const THUMB_MIN_HEIGHT = 40;
const RAIL_WIDTH = 14;

export function CustomScrollbar() {
  const railRef = useRef(null);
  const thumbRef = useRef(null);
  const [thumbHeight, setThumbHeight] = useState(80);
  const [thumbTop, setThumbTop] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [visible, setVisible] = useState(true);
  const dragStartY = useRef(0);
  const dragStartScrollTop = useRef(0);

  const updateThumb = useCallback(() => {
    const el = document.querySelector('.main-scroll');
    if (!el) return;
    const { scrollHeight, clientHeight, scrollTop } = el;
    if (scrollHeight <= clientHeight) {
      setVisible(false);
      return;
    }
    setVisible(true);
    if (!railRef.current) return;
    const railHeight = railRef.current.clientHeight;
    const ratio = clientHeight / scrollHeight;
    const height = Math.max(THUMB_MIN_HEIGHT, Math.round(railHeight * ratio));
    setThumbHeight(height);
    const maxTop = railHeight - height;
    const thumbTopPercent = scrollTop / (scrollHeight - clientHeight);
    setThumbTop(thumbTopPercent * maxTop);
  }, []);

  useEffect(() => {
    const el = document.querySelector('.main-scroll');
    if (!el) return;
    updateThumb();
    el.addEventListener('scroll', updateThumb);
    const ro = new ResizeObserver(updateThumb);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateThumb);
      ro.disconnect();
    };
  }, [updateThumb]);

  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartY.current = e.clientY;
    const el = document.querySelector('.main-scroll');
    if (el) dragStartScrollTop.current = el.scrollTop;
  };

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => {
      const el = document.querySelector('.main-scroll');
      if (!el || !railRef.current) return;
      const railHeight = railRef.current.clientHeight;
      const deltaY = e.clientY - dragStartY.current;
      const ratio = (el.scrollHeight - el.clientHeight) / (railHeight - thumbHeight);
      el.scrollTop = dragStartScrollTop.current + deltaY * ratio;
    };
    const onUp = () => setIsDragging(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isDragging, thumbHeight]);

  const handleRailClick = (e) => {
    if (thumbRef.current && thumbRef.current.contains(e.target)) return;
    const el = document.querySelector('.main-scroll');
    if (!el || !railRef.current) return;
    const rect = railRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const railHeight = railRef.current.clientHeight;
    const ratio = (el.scrollHeight - el.clientHeight) / Math.max(1, railHeight - thumbHeight);
    const targetScroll = (y - thumbHeight / 2) * ratio;
    el.scrollTo({ top: Math.max(0, targetScroll), behavior: 'smooth' });
  };

  if (!visible) return null;

  return (
    <div
      ref={railRef}
      className="custom-scrollbar-rail"
      role="scrollbar"
      aria-valuenow={thumbTop}
      aria-valuemin={0}
      aria-valuemax={100}
      onClick={handleRailClick}
    >
      <div
        ref={thumbRef}
        className="custom-scrollbar-thumb"
        style={{ height: thumbHeight, transform: `translateY(${thumbTop}px)` }}
        onMouseDown={handleThumbMouseDown}
      />
    </div>
  );
}
